import { useEffect, useState, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Alert,
  ApiError,
  Badge,
  Spinner,
  apiFetch,
  fmtLongDate,
  type PortalDashboardMedicareConnection,
  type PortalMe,
} from "@dearbill/shared";
import { HomeCard } from "./home/HomeCard";
import type { DashboardOutletContext } from "./MemberDashboard";
import { CloseAccountCard } from "./settings/CloseAccountCard";
import { PreferencesCard } from "./settings/PreferencesCard";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** The Blue Button data permits the consent card lists; one grant covers all of them. */
const CONSENT_PERMITS = [
  "Prescription Drug Claims history",
  "Part D Enrollment and Plan data",
  "Plan premium and deductible history",
];

const FIELD_LABEL = "text-eyebrow-sm text-slate-teal uppercase";
const FIELD_VALUE =
  "border-haze bg-ice text-body-sm text-midnight rounded-lg border px-4 py-3 font-medium";
const NOTE = "text-body-sm text-slate-teal";

/**
 * The Settings content, rendered inside `MemberLayout` from the dashboard the
 * outlet context carries plus its own `GET /api/portal/me` read, which supplies
 * the contact email and phone. The page title is the `<h1>`; each card carries
 * an `<h2>`. Until `/me` lands the cards give way to a spinner; a 401 signs
 * the member out and any other failure shows the error alert in their place.
 * The profile fields are read-only (the portal has no profile-edit endpoint)
 * and the Medicare consent is managed on Medicare.gov; the notification
 * switches save themselves and the closure card requests or cancels the
 * closure. The preferences are the patient's, so a caregiver session, whose
 * `/me` carries the caregiver's own contact, gets no Communication
 * Preferences card.
 */
export function SettingsScreen(): ReactNode {
  const { data, reload, session, onSignOut } = useOutletContext<DashboardOutletContext>();
  const [me, setMe] = useState<PortalMe | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PortalMe>("/api/portal/me", {}, { baseUrl: API_BASE, token: session })
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSignOut();
          return;
        }
        setError("We couldn't load your settings. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [session, onSignOut]);

  let content: ReactNode;
  if (error) {
    content = <Alert tone="error" title={error} className="max-w-2xl" />;
  } else if (!me) {
    content = (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Loading your settings…</span>
      </div>
    );
  } else {
    const name = [data.firstName, data.lastName]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");
    content = (
      <>
        <ProfileCard name={name} email={me.email} connected={data.medicareConnection.connected} />
        <ConsentCard connection={data.medicareConnection} />
        {data.role === "member" && me.preferences !== null && (
          <PreferencesCard
            initial={me.preferences}
            email={me.email}
            phone={me.phone}
            session={session}
            onSignOut={onSignOut}
          />
        )}
        <CloseAccountCard
          closure={data.accountClosure}
          readOnly={data.role === "caregiver"}
          session={session}
          reload={reload}
          onSignOut={onSignOut}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <h1 className="text-h2 text-midnight font-serif">Dashboard Settings</h1>
        <p className={NOTE}>
          Review your digital privacy consents and select notification preference delivery methods.
        </p>
      </div>
      {content}
    </>
  );
}

/** A read-only profile field: an uppercase label, the value in an Ice box and an optional helper line. */
function ProfileField({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper?: string | undefined;
}): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      <p className={FIELD_LABEL}>{label}</p>
      <p className={FIELD_VALUE}>{value}</p>
      {helper !== undefined && <p className={NOTE}>{helper}</p>}
    </div>
  );
}

/**
 * Name and contact email. The name is labeled as sourced from Medicare only
 * while Blue Button is connected: otherwise it is the member's own entry.
 */
function ProfileCard({
  name,
  email,
  connected,
}: {
  name: string;
  email: string | null;
  connected: boolean;
}): ReactNode {
  return (
    <HomeCard title="Profile Information" titleSize="lg">
      <div className="grid gap-6 md:grid-cols-2">
        <ProfileField
          label={connected ? "Member name (Sourced from Medicare)" : "Member name"}
          value={name}
          helper={
            connected
              ? "This name matches your legal federal Medicare records and cannot be changed here."
              : undefined
          }
        />
        <ProfileField
          label="Contact email (Used for notifications)"
          value={email ?? "Not on file"}
          helper="We use this email to coordinate active federal assistance updates."
        />
      </div>
    </HomeCard>
  );
}

/**
 * The Blue Button consent, keyed on the one `connected` flag because a grant
 * covers every permit at once. The footer's date is the connection's creation
 * time, so it shows only while connected.
 */
function ConsentCard({ connection }: { connection: PortalDashboardMedicareConnection }): ReactNode {
  const { connected } = connection;
  return (
    <HomeCard
      title="Privacy & Data Consent Status"
      titleSize="lg"
      action={
        <Badge tone={connected ? "active" : "error"}>
          {connected ? "Active consent" : "No active consent"}
        </Badge>
      }
    >
      <p className={NOTE}>
        To calculate your savings correctly, DearBill requires active permission access from your
        connected Medicare account. Below is the current status of authorized permits.
      </p>
      <dl className="flex flex-col gap-3">
        {CONSENT_PERMITS.map((permit) => (
          <div
            key={permit}
            className="border-haze bg-ice text-body-sm flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border px-4 py-3 font-semibold"
          >
            <dt className="text-midnight">{permit}</dt>
            <dd className={connected ? "text-forest" : "text-error-red"}>
              {connected ? "Authorized (Sourced from Medicare)" : "Not Authorized"}
            </dd>
          </div>
        ))}
      </dl>
      {connected && (
        <p className={NOTE}>
          Consent granted on {fmtLongDate(connection.connectedAt)}. Revocations must be initiated on
          Medicare.gov.
        </p>
      )}
    </HomeCard>
  );
}
